import { AxiosProgressEvent } from 'axios';
import { EXPRESS_API } from 'configs/api/expressApi';
import { errorToast } from 'lib/components/toasts/error';
import { successToast } from 'lib/components/toasts/success';
import { InventoryItem } from 'lib/context';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

export interface CreateVideoDataTags {
  value: string | number;
  label: string;
  __isNew__?: boolean;
}

interface CreateVideoAttributes {
  value: string;
  attributeId: number;
}

interface CreateVideoRequestRequest {
  advisorId?: number;
  videoType?: string;
  repairOrderNumber?: string;
  vin?: string;
  note?: string;
  customerName?: string;
}

interface VideoData {
  title: string;
  filename: string;
  id?: number | string;
  isFreemium?: boolean;
  isGuide?: boolean;
  folder?: string | number;
  videoTags?: CreateVideoDataTags[];
  videoAttributeValues?: CreateVideoAttributes[];
  videoRequest?: CreateVideoRequestRequest;
}

export interface CreateVideoResponse {
  title: string;
  flvName: string;
  userId: number;
  customerId: number;
  totalViews: number;
  folderName: string;
  processing: number;
  recordDate: string;
  recordDateUnix: number;
  serverID: number;
  animatedGifPath: string;
  templateText: string;
  forwardCount: number;
  androidLocation: string;
  notes: string;
  securityCode: string;
  sendReply: number;
  resellerId: number;
  recorderId: number;
  isGuide: number;
  apiProviderId: null;
  deletedAt: null;
  createdAt: Date;
  updatedAt: Date;
  id: number;
  merged: number;
  duplicated: number;
  isVoiceover: number;
  videoReplyEnabled: number;
  videoRequest: VideoRequest;
  videoId: string;
}

interface VideoRequest {
  videoId: number;
  advisorId: number;
  userId: number;
  customerId: number;
  customerName: string;
  status: string;
  videoType: string;
  automotiveDepartment: number;
  emailStatus: string;
  deletedAt: null;
  videoRequestId: number;
  createdAt: Date;
  advisor: Advisor;
  vin?: string;
  inventoryItem?: InventoryItem;
}

interface Advisor {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phone2: string;
  website: string;
  title: string;
}

interface IProgress {
  loaded: number;
  total: number;
  percentage: number;
}

const createVideo = async (
  props: VideoData,
  onProgress?: (progress: IProgress) => void
): Promise<CreateVideoResponse> => {
  const {
    title,
    filename,
    isFreemium,
    isGuide,
    folder,
    id,
    videoAttributeValues,
    videoRequest,
    videoTags,
  } = props;

  const config = {
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (onProgress) {
        const { loaded, total } = progressEvent;
        if (!total) return;
        const percentCompleted = Math.round((loaded * 100) / total);
        onProgress({
          loaded: Math.floor(loaded / 1024),
          total: Math.floor(total / 1024),
          percentage: percentCompleted,
        });
      }
    },
  };

  const response = await EXPRESS_API.post(
    `/videos`,
    {
      filename,
      title,
      isFreemium,
      isGuide: !!isGuide,
      folder: folder || 'Primary',
      videoAttributeValues,
      videoRequest,
      videoTags,
    },
    config
  );

  return {
    ...response.data,
    ...(id !== undefined ? { id: id, videoId: response.data.id } : {}),
  };
};

export const useCreateVideoMutation = () => {
  const { t } = useTranslation();
  const [createProgress, setCreateProgress] = useState({
    loaded: 0,
    total: 0,
    percentage: 0,
  });
  return {
    ...useMutation((data: VideoData) => createVideo(data, setCreateProgress), {
      onSuccess: () => {
        successToast({ title: t('Video successfully created!') });
      },
      onError: () => {
        errorToast({
          title: t('Failed to create video. Please try again later.'),
        });
      },
    }),
    progress: createProgress,
  };
};
